import { useEffect, useRef, useState } from "react";

interface CategoryInfo {
  id: string;
  title: string;
  subtitle: string;
  desc: string;
  img: string;
  accentColor: string;
}

const categoryInfoMap: Record<string, CategoryInfo> = {
  dzi: {
    id: "dzi",
    title: "西藏天珠",
    subtitle: "千年傳承的守護之珠",
    desc: "天珠被視為藏文化中具神聖意義的護符，歷經千年流傳，象徵守護、智慧、祝福與能量平衡。每一顆天珠都是獨一無二的，承載著古老文明的智慧與祝福。",
    img: "https://readdy.ai/api/search-image?query=collection%20of%20Tibetan%20dzi%20beads%20ancient%20agate%20stones%20arranged%20on%20aged%20wooden%20surface%20warm%20golden%20light%20cultural%20heritage%20photography%20atmospheric%20high%20detail%20texture&width=1200&height=500&seq=cat_dzi001&orientation=landscape",
    accentColor: "#8b6f47",
  },
  crystal: {
    id: "crystal",
    title: "天然水晶礦石",
    subtitle: "大自然孕育的能量寶石",
    desc: "大自然歷經億萬年孕育的礦石與水晶，蘊含著獨特的能量頻率與美麗紋理。不同水晶象徵不同的祝福，找到與你頻率共鳴的那一顆。",
    img: "https://readdy.ai/api/search-image?query=various%20natural%20crystals%20gemstones%20collection%20amethyst%20quartz%20obsidian%20arranged%20on%20white%20marble%20surface%20soft%20natural%20light%20elegant%20minimalist%20style%20high%20detail%20texture&width=1200&height=500&seq=cat_cry001&orientation=landscape",
    accentColor: "#7a6a9a",
  },
  jade: {
    id: "jade",
    title: "翡翠玉石",
    subtitle: "東方文化的吉祥象徵",
    desc: "玉石自古被視為純潔、吉祥與守護的象徵。瓏靖精選各類翡翠玉石，將天然美感與文化寓意融合，呈現溫潤典雅的能量飾品。",
    img: "https://readdy.ai/api/search-image?query=jade%20jadeite%20green%20jewelry%20collection%20bracelet%20pendant%20necklace%20on%20white%20marble%20with%20green%20leaves%20soft%20natural%20light%20elegant%20oriental%20style%20high%20detail%20texture&width=1200&height=500&seq=cat_jade001&orientation=landscape",
    accentColor: "#4a7a52",
  },
  thangka: {
    id: "thangka",
    title: "唐卡佛牌",
    subtitle: "信仰與藝術的結合",
    desc: "唐卡與佛牌在藏傳文化中具有深厚的宗教與藝術價值，每一件作品承載著信仰、祝福與文化傳承，是心靈守護與藝術收藏的珍品。",
    img: "https://readdy.ai/api/search-image?query=Tibetan%20thangka%20painting%20Buddhist%20art%20colorful%20mandala%20traditional%20artwork%20collection%20on%20dark%20background%20warm%20golden%20light%20cultural%20heritage%20photography%20high%20detail&width=1200&height=500&seq=cat_thk001&orientation=landscape",
    accentColor: "#c9a96e",
  },
};

interface Props {
  categoryId: string;
}

export default function CategoryBanner({ categoryId }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, [categoryId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const info = categoryInfoMap[categoryId];
  if (!info) return null;

  return (
    <div
      ref={ref}
      className={`relative w-full h-52 md:h-64 rounded-2xl overflow-hidden mb-12 transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
    >
      <img
        src={info.img}
        alt={info.title}
        className="w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#1e1a16]/80 via-[#1e1a16]/50 to-transparent"></div>
      <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-12">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-8 h-px" style={{ backgroundColor: info.accentColor }}></span>
          <span
            className="text-[1rem] tracking-[0.3em] font-medium"
            style={{ color: info.accentColor, fontFamily: "'Noto Serif TC', serif" }}
          >
            {info.subtitle}
          </span>
        </div>
        <h2
          className="text-white font-bold mb-3 leading-[1.3]"
          style={{
            fontSize: "clamp(1.5rem, 3vw, 2.125rem)",
            fontFamily: "'Noto Serif TC', serif",
          }}
        >
          {info.title}
        </h2>
        <p
          className="text-white/65 max-w-lg leading-[1.75] hidden md:block"
          style={{
            fontSize: "1.1875rem",
            fontFamily: "'Noto Serif TC', serif",
          }}
        >
          {info.desc}
        </p>
      </div>
    </div>
  );
}
